import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Users, ChevronDown, ChevronRight, Pencil, UserPlus, Trash2, Archive, Eye, LogOut, Wrench } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { isAdmin, isHR } from '@/lib/auth';
import api from '@/lib/api';

const ROLE_COLORS = {
  director: 'bg-amber-100 text-amber-800',
  director_hr: 'bg-purple-100 text-purple-800',
  hr: 'bg-indigo-100 text-indigo-800',
  manager: 'bg-blue-100 text-blue-800',
  employee: 'bg-slate-100 text-slate-700',
};

function buildFlatTree(employees) {
  const root = employees.find(e => e.org_role === 'director' && e.reporting_manager === e.name);
  if (!root) return [];
  const result = [];
  const visited = new Set();
  const queue = [{ emp: root, depth: 0 }];
  while (queue.length > 0) {
    const { emp, depth } = queue.shift();
    if (visited.has(emp.id)) continue;
    visited.add(emp.id);
    const children = employees.filter(e => e.reporting_manager === emp.name && e.id !== emp.id && !visited.has(e.id));
    result.push({ ...emp, depth, childCount: children.length });
    const childItems = children.map(c => ({ emp: c, depth: depth + 1 })).reverse();
    queue.unshift(...childItems.reverse());
  }
  return result;
}

export default function TeamMasterPage() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editEmp, setEditEmp] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    name: '', designation: '', department: '', email: '', phone: '',
    date_of_joining: '', location: '', employment_type: 'Full-time',
    reporting_manager: '', org_role: 'employee', gender: '', auto_onboard: true,
  });
  const [createdResult, setCreatedResult] = useState(null);
  const [exitOpen, setExitOpen] = useState(false);
  const [exitEmp, setExitEmp] = useState(null);
  const [exitForm, setExitForm] = useState({ exit_reason: '', last_working_date: '', exit_remarks: '' });
  const [archives, setArchives] = useState([]);
  const [viewArchive, setViewArchive] = useState(null);
  const [activeTab, setActiveTab] = useState('org-chart');
  const admin = isAdmin() || isHR();

  const DEPARTMENTS = [...new Set(employees.map(e => e.department).filter(Boolean))].sort();
  const LOCATIONS = [...new Set(employees.map(e => e.location).filter(Boolean))].sort();
  const ORG_ROLES = ['director', 'director_hr', 'hr', 'manager', 'employee'];

  const loadData = useCallback(() => {
    api.get('/api/org-chart').then(r => {
      setEmployees(r.data);
      setLoading(false);
    }).catch(() => { toast.error('Failed to load'); setLoading(false); });
  }, []);

  const loadArchives = useCallback(() => {
    if (!admin) return;
    api.get('/api/archived-employees').then(r => setArchives(r.data || [])).catch(() => {});
  }, [admin]);

  useEffect(() => { loadData(); loadArchives(); }, [loadData, loadArchives]);

  const toggleCollapse = useCallback((name) => {
    setCollapsed(prev => ({ ...prev, [name]: !prev[name] }));
  }, []);

  const openEdit = (node) => {
    setEditEmp(node);
    setEditForm({
      name: node.name || '',
      department: node.department || '',
      designation: node.designation || '',
      org_role: node.org_role || 'employee',
      reporting_manager: node.reporting_manager || '',
      gender: node.gender || '',
    });
  };

  const handleSave = async () => {
    if (!editEmp) return;
    setSaving(true);
    try {
      // Update employee details via main employee endpoint
      const updates = {};
      if (editForm.name && editForm.name !== editEmp.name) updates.name = editForm.name;
      if (editForm.department && editForm.department !== editEmp.department) updates.department = editForm.department;
      if (editForm.designation && editForm.designation !== editEmp.designation) updates.designation = editForm.designation;
      if (editForm.org_role && editForm.org_role !== editEmp.org_role) updates.org_role = editForm.org_role;
      if (editForm.gender && editForm.gender !== editEmp.gender) updates.gender = editForm.gender;

      if (Object.keys(updates).length > 0) {
        await api.put(`/api/employees/${editEmp.id}`, updates);
      }
      // Update reporting manager via org-chart endpoint
      if (editForm.reporting_manager && editForm.reporting_manager !== editEmp.reporting_manager) {
        await api.put(`/api/org-chart/${editEmp.id}`, { reporting_manager: editForm.reporting_manager });
      }
      toast.success('Employee updated');
      loadData();
      setEditEmp(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
    setSaving(false);
  };

  const [repairing, setRepairing] = useState(false);

  const handleRepairDuplicateIds = async () => {
    if (!window.confirm('This will detect and reassign duplicate employee IDs. The OLDEST record keeps its code; newer duplicates get fresh codes. Their username/salary/onboarding rows are auto-updated. Continue?')) return;
    setRepairing(true);
    try {
      const { data } = await api.post('/api/employees/repair-duplicate-codes');
      if (!data.repaired || data.repaired.length === 0) {
        toast.success('No duplicates found — all IDs are unique.');
      } else {
        const summary = data.repaired.map(r => `${r.name}: ${r.old_code} → ${r.new_code}`).join('\n');
        window.alert(`Repaired ${data.repaired.length} duplicate(s):\n\n${summary}`);
        toast.success(`Fixed ${data.repaired.length} duplicate ID(s)`);
        loadData();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to run repair');
    }
    setRepairing(false);
  };


  const handleAddEmployee = async () => {
    if (!addForm.name.trim()) { toast.error('Name is required'); return; }
    if (!addForm.designation.trim()) { toast.error('Designation is required'); return; }
    if (!addForm.department) { toast.error('Department is required'); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/api/employees', addForm);
      setCreatedResult(data);
      toast.success(`Employee ${data.employee_code} created successfully`);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create employee'); }
    setSaving(false);
  };

  const resetAddForm = () => {
    setAddForm({
      name: '', designation: '', department: '', email: '', phone: '',
      date_of_joining: '', location: '', employment_type: 'Full-time',
      reporting_manager: '', org_role: 'employee', gender: '', auto_onboard: true,
    });
    setCreatedResult(null);
  };

  const flatTree = buildFlatTree(employees);

  const handleDeleteEmployee = (emp) => {
    setExitEmp(emp);
    setExitForm({ exit_reason: '', last_working_date: '', exit_remarks: '' });
    setExitOpen(true);
  };

  const handleInitiateExit = async () => {
    if (!exitEmp) return;
    if (!exitForm.exit_reason) { toast.error('Select an exit reason'); return; }
    setSaving(true);
    try {
      // Initiate exit first (marks employee for exit form)
      await api.post(`/api/employees/${exitEmp.id}/initiate-exit`, exitForm);
      toast.success(`Exit initiated for ${exitEmp.name}. Employee can now fill exit form.`);
      setExitOpen(false);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  const handleArchiveEmployee = async () => {
    if (!exitEmp) return;
    if (!window.confirm(`Archive ${exitEmp.name} (${exitEmp.employee_code})? They will be moved to ex-employee records and can no longer login.`)) return;
    setSaving(true);
    try {
      await api.delete(`/api/employees/${exitEmp.id}`);
      toast.success(`${exitEmp.name} archived to ex-employee records`);
      setExitOpen(false);
      loadData();
      loadArchives();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };
  const depts = {};
  employees.forEach(e => {
    if (!depts[e.department]) depts[e.department] = 0;
    depts[e.department]++;
  });
  const managers = employees.filter(e => ['manager', 'director', 'director_hr', 'hr'].includes(e.org_role));

  // Filter visible nodes based on collapsed state
  const isHidden = (node) => {
    let parent = employees.find(e => e.name === node.reporting_manager && e.name !== node.name);
    while (parent) {
      if (collapsed[parent.name]) return true;
      const next = employees.find(e => e.name === parent.reporting_manager && e.name !== parent.name);
      parent = next;
    }
    return false;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-[#0D2850]" /></div>;

  return (
    <div data-testid="team-master-page">
      <Badge className="bg-[#0D2850] text-white text-[10px] mb-3 hover:bg-[#0D2850]">// TEAM MASTER</Badge>
      <div className="flex items-center justify-between mb-1">
        <div>
          <h1 className="text-2xl font-semibold text-[#0D2850] tracking-tight">Organization Chart</h1>
          <p className="text-sm text-slate-500 mt-1">View and manage the reporting hierarchy</p>
        </div>
        {admin && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50" onClick={handleRepairDuplicateIds} disabled={repairing} data-testid="repair-duplicates-btn" title="Detects and reassigns duplicate employee IDs (Admin utility — safe to run any time)">
              <Wrench className="w-4 h-4" /> {repairing ? 'Repairing…' : 'Repair Duplicate IDs'}
            </Button>
            <Button className="bg-[#0D2850] hover:bg-[#0B2246] gap-1.5" onClick={() => { resetAddForm(); setAddOpen(true); }} data-testid="add-employee-btn">
              <UserPlus className="w-4 h-4" /> Add Employee
            </Button>
          </div>
        )}
      </div>

      {admin && (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="bg-white border border-slate-200 h-9">
            <TabsTrigger value="org-chart" className="data-[state=active]:bg-[#0D2850] data-[state=active]:text-white text-xs" data-testid="tab-org-chart">
              <Users className="w-3.5 h-3.5 mr-1" /> Active Employees
            </TabsTrigger>
            <TabsTrigger value="archived" className="data-[state=active]:bg-[#0D2850] data-[state=active]:text-white text-xs" data-testid="tab-archived">
              <Archive className="w-3.5 h-3.5 mr-1" /> Ex-Employees
              {archives.length > 0 && <Badge className="ml-1 bg-slate-200 text-slate-600 text-[10px] hover:bg-slate-200">{archives.length}</Badge>}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="org-chart" className="mt-3">

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3 mb-6">
        {Object.entries(depts).map(([d, c]) => (
          <Card key={d} className="border-slate-200">
            <CardContent className="p-3">
              <p className="text-[10px] font-medium text-slate-400 uppercase">{d}</p>
              <p className="text-lg font-semibold text-[#0D2850]">{c}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border-slate-200">
        <CardHeader className="py-3 px-4 pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Users className="w-4 h-4 text-slate-500" /> Reporting Hierarchy
            <span className="text-xs font-normal text-slate-400 ml-2">{employees.length} employees</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          {flatTree.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No data</p>}
          {flatTree.filter(n => !isHidden(n)).map(node => {
            const roleColor = ROLE_COLORS[node.org_role] || ROLE_COLORS.employee;
            const roleLabel = (node.org_role || 'employee').replace('_', ' ');
            const initials = node.name.split(' ').map(w => w[0]).slice(0, 2).join('');
            const isCollapsed = collapsed[node.name];
            return (
              <div
                key={node.id}
                style={{ paddingLeft: node.depth * 28 }}
                className="flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-slate-50 group"
                data-testid={`org-node-${node.id}`}
              >
                {node.childCount > 0 ? (
                  <button onClick={() => toggleCollapse(node.name)} className="w-5 h-5 flex items-center justify-center text-slate-400">
                    {isCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  </button>
                ) : <span className="w-5" />}
                <div className="w-7 h-7 rounded-full bg-[#0D2850] text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                  {initials}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-[#0D2850]">{node.name}</span>
                  <span className="text-[10px] text-slate-400 ml-2">{node.designation}</span>
                </div>
                <Badge className={`text-[9px] ${roleColor} hover:opacity-90`}>{roleLabel}</Badge>
                <span className="text-[10px] text-slate-400 hidden sm:inline">{node.department}</span>
                {node.childCount > 0 && (
                  <span className="text-[9px] text-slate-400 bg-slate-100 px-1.5 rounded">{node.childCount}</span>
                )}
                {admin && (
                  <>
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100" onClick={() => openEdit(node)} data-testid={`edit-rm-${node.id}`}>
                      <Pencil className="w-3 h-3 text-slate-400" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100" onClick={() => handleDeleteEmployee(node)} data-testid={`exit-emp-${node.id}`} title="Initiate Exit">
                      <LogOut className="w-3 h-3 text-red-400" />
                    </Button>
                  </>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Close org-chart tab + Archived tab */}
          </TabsContent>

          <TabsContent value="archived" className="mt-3">
            {archives.length === 0 ? (
              <Card className="border-slate-200">
                <CardContent className="p-10 text-center">
                  <Archive className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-400">No archived employees</p>
                  <p className="text-xs text-slate-400 mt-1">When employees exit, their records will appear here</p>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-slate-200">
                <CardContent className="px-0 py-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Code</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Department</TableHead>
                        <TableHead>Designation</TableHead>
                        <TableHead>Exit Reason</TableHead>
                        <TableHead>Last Working Day</TableHead>
                        <TableHead>Archived On</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {archives.map(a => {
                        const emp = a.employee || {};
                        return (
                          <TableRow key={a.id} data-testid={`archive-${a.id}`}>
                            <TableCell className="text-xs font-mono">{emp.employee_code}</TableCell>
                            <TableCell className="text-sm font-medium">{emp.name}</TableCell>
                            <TableCell className="text-sm text-slate-600">{emp.department}</TableCell>
                            <TableCell className="text-sm text-slate-500">{emp.designation}</TableCell>
                            <TableCell><Badge variant="outline" className="text-xs">{emp.exit_reason || '--'}</Badge></TableCell>
                            <TableCell className="text-sm">{emp.last_working_date || '--'}</TableCell>
                            <TableCell className="text-xs text-slate-400">{a.archived_at ? new Date(a.archived_at).toLocaleDateString('en-IN') : '--'}</TableCell>
                            <TableCell>
                              <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={() => setViewArchive(a)} data-testid={`view-archive-${a.id}`}>
                                <Eye className="w-3 h-3" /> View
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      )}

      {/* Edit Employee Dialog */}
      <Dialog open={!!editEmp} onOpenChange={() => setEditEmp(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Edit Employee Details</DialogTitle></DialogHeader>
          {editEmp && (
            <div className="space-y-4 mt-2">
              <div>
                <Label>Name</Label>
                <Input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} data-testid="edit-emp-name" />
              </div>
              <div>
                <Label>Department</Label>
                <Select value={editForm.department} onValueChange={v => setEditForm({...editForm, department: v})}>
                  <SelectTrigger data-testid="edit-emp-dept"><SelectValue placeholder="Select department" /></SelectTrigger>
                  <SelectContent>
                    {DEPARTMENTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Designation</Label>
                <Input value={editForm.designation} onChange={e => setEditForm({...editForm, designation: e.target.value})} data-testid="edit-emp-designation" />
              </div>
              <div>
                <Label>Org Role</Label>
                <Select value={editForm.org_role} onValueChange={v => setEditForm({...editForm, org_role: v})}>
                  <SelectTrigger data-testid="edit-emp-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ORG_ROLES.map(r => <SelectItem key={r} value={r}>{r.replace('_', ' ')}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Reporting Manager</Label>
                <Select value={editForm.reporting_manager} onValueChange={v => setEditForm({...editForm, reporting_manager: v})}>
                  <SelectTrigger data-testid="edit-emp-manager"><SelectValue placeholder="Select manager..." /></SelectTrigger>
                  <SelectContent>
                    {managers.filter(m => m.id !== editEmp?.id).map(m => (
                      <SelectItem key={m.id} value={m.name}>{m.name} ({m.department})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Gender</Label>
                <Select value={editForm.gender || ''} onValueChange={v => setEditForm({...editForm, gender: v})}>
                  <SelectTrigger data-testid="edit-emp-gender"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleSave} disabled={saving} className="w-full bg-[#0D2850] hover:bg-[#0B2246]" data-testid="save-emp-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Employee Dialog */}
      <Dialog open={addOpen} onOpenChange={(v) => { setAddOpen(v); if (!v) resetAddForm(); }}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{createdResult ? 'Employee Created' : 'Add New Employee'}</DialogTitle></DialogHeader>
          {createdResult ? (
            <div className="space-y-4 mt-2" data-testid="employee-created-result">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                  <UserPlus className="w-6 h-6 text-emerald-600" />
                </div>
                <p className="text-lg font-semibold text-emerald-800">{addForm.name}</p>
                <p className="text-sm text-emerald-600">has been added to the organization</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Employee Code</span>
                  <span className="font-mono font-semibold text-[#0D2850]" data-testid="created-emp-code">{createdResult.employee_code}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Login Username</span>
                  <span className="font-mono font-semibold text-[#0D2850]" data-testid="created-username">{createdResult.username}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Default Password</span>
                  <span className="font-mono font-semibold text-[#0D2850]" data-testid="created-password">{createdResult.default_password}</span>
                </div>
                {createdResult.onboarding_id && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Onboarding</span>
                    <Badge className="bg-emerald-100 text-emerald-700 text-xs">Auto-started</Badge>
                  </div>
                )}
              </div>
              <p className="text-xs text-slate-400">Share the login credentials with the new employee. They will be asked to change their password on first login.</p>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => { resetAddForm(); }}>Add Another</Button>
                <Button className="flex-1 bg-[#0D2850] hover:bg-[#0B2246]" onClick={() => { setAddOpen(false); resetAddForm(); }}>Done</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs">Full Name <span className="text-red-500">*</span></Label>
                  <Input value={addForm.name} onChange={e => setAddForm({...addForm, name: e.target.value})} placeholder="e.g. Rahul Kumar" data-testid="add-emp-name" />
                </div>
                <div>
                  <Label className="text-xs">Designation <span className="text-red-500">*</span></Label>
                  <Input value={addForm.designation} onChange={e => setAddForm({...addForm, designation: e.target.value})} placeholder="e.g. Sales Executive" data-testid="add-emp-designation" />
                </div>
                <div>
                  <Label className="text-xs">Department <span className="text-red-500">*</span></Label>
                  <Select value={addForm.department} onValueChange={v => setAddForm({...addForm, department: v})}>
                    <SelectTrigger data-testid="add-emp-department"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      {DEPARTMENTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input type="email" value={addForm.email} onChange={e => setAddForm({...addForm, email: e.target.value})} placeholder="email@company.com" data-testid="add-emp-email" />
                </div>
                <div>
                  <Label className="text-xs">Phone</Label>
                  <Input value={addForm.phone} onChange={e => setAddForm({...addForm, phone: e.target.value})} placeholder="+91 9876543210" data-testid="add-emp-phone" />
                </div>
                <div>
                  <Label className="text-xs">Date of Joining</Label>
                  <Input type="date" value={addForm.date_of_joining} onChange={e => setAddForm({...addForm, date_of_joining: e.target.value})} data-testid="add-emp-doj" />
                </div>
                <div>
                  <Label className="text-xs">Location</Label>
                  <Select value={addForm.location} onValueChange={v => setAddForm({...addForm, location: v})}>
                    <SelectTrigger data-testid="add-emp-location"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      {LOCATIONS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Employment Type</Label>
                  <Select value={addForm.employment_type} onValueChange={v => setAddForm({...addForm, employment_type: v})}>
                    <SelectTrigger data-testid="add-emp-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Full-time">Full-time</SelectItem>
                      <SelectItem value="Part-time">Part-time</SelectItem>
                      <SelectItem value="Contract">Contract</SelectItem>
                      <SelectItem value="Intern">Intern</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Gender</Label>
                  <Select value={addForm.gender} onValueChange={v => setAddForm({...addForm, gender: v})}>
                    <SelectTrigger data-testid="add-emp-gender"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Org Role</Label>
                  <Select value={addForm.org_role} onValueChange={v => setAddForm({...addForm, org_role: v})}>
                    <SelectTrigger data-testid="add-emp-org-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ORG_ROLES.map(r => <SelectItem key={r} value={r}>{r.replace('_', ' ')}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Reporting Manager</Label>
                  <Select value={addForm.reporting_manager} onValueChange={v => setAddForm({...addForm, reporting_manager: v})}>
                    <SelectTrigger data-testid="add-emp-manager"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      {managers.map(m => <SelectItem key={m.id} value={m.name}>{m.name} ({m.department})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3 mt-2">
                <div>
                  <p className="text-sm font-medium text-[#0B1B33]">Auto-start Onboarding</p>
                  <p className="text-xs text-slate-400">Begin the onboarding workflow immediately after creation</p>
                </div>
                <Switch checked={addForm.auto_onboard} onCheckedChange={v => setAddForm({...addForm, auto_onboard: v})} data-testid="add-emp-auto-onboard" />
              </div>
              <Button onClick={handleAddEmployee} disabled={saving} className="w-full bg-[#0D2850] hover:bg-[#0B2246] mt-2" data-testid="submit-add-employee">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Employee'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Exit Initiation Dialog */}
      <Dialog open={exitOpen} onOpenChange={v => { if (!v) setExitOpen(false); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Initiate Employee Exit</DialogTitle></DialogHeader>
          {exitEmp && (
            <div className="space-y-4 mt-2">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm font-medium text-red-800">{exitEmp.name}</p>
                <p className="text-xs text-red-600">{exitEmp.employee_code} &middot; {exitEmp.department} &middot; {exitEmp.designation}</p>
              </div>
              <div>
                <Label className="text-xs">Exit Reason <span className="text-red-500">*</span></Label>
                <Select value={exitForm.exit_reason} onValueChange={v => setExitForm({...exitForm, exit_reason: v})}>
                  <SelectTrigger data-testid="exit-reason-select"><SelectValue placeholder="Select reason..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Resignation">Resignation</SelectItem>
                    <SelectItem value="Termination">Termination</SelectItem>
                    <SelectItem value="Retirement">Retirement</SelectItem>
                    <SelectItem value="End of Contract">End of Contract</SelectItem>
                    <SelectItem value="Mutual Separation">Mutual Separation</SelectItem>
                    <SelectItem value="Absconding">Absconding</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Last Working Date</Label>
                <Input type="date" value={exitForm.last_working_date} onChange={e => setExitForm({...exitForm, last_working_date: e.target.value})} data-testid="exit-lwd-input" />
              </div>
              <div>
                <Label className="text-xs">Remarks</Label>
                <Textarea value={exitForm.exit_remarks} onChange={e => setExitForm({...exitForm, exit_remarks: e.target.value})} placeholder="Any additional notes..." rows={3} data-testid="exit-remarks-input" />
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={handleInitiateExit} disabled={saving} className="flex-1 bg-amber-600 hover:bg-amber-700 text-white" data-testid="initiate-exit-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Initiate Exit'}
                </Button>
                <Button onClick={handleArchiveEmployee} disabled={saving} variant="destructive" className="flex-1" data-testid="archive-emp-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Archive Now'}
                </Button>
              </div>
              <p className="text-[10px] text-slate-400 leading-tight"><b>Initiate Exit:</b> Marks employee for exit — they can fill an exit form. <b>Archive Now:</b> Immediately moves to ex-employee records.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View Archived Employee Dialog */}
      <Dialog open={!!viewArchive} onOpenChange={() => setViewArchive(null)}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Ex-Employee Record</DialogTitle></DialogHeader>
          {viewArchive && (() => {
            const emp = viewArchive.employee || {};
            const exitF = viewArchive.exit_form;
            return (
              <div className="space-y-4 mt-2">
                <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm"><span className="text-slate-500">Name</span><span className="font-medium">{emp.name}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-slate-500">Code</span><span className="font-mono">{emp.employee_code}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-slate-500">Department</span><span>{emp.department}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-slate-500">Designation</span><span>{emp.designation}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-slate-500">Date of Joining</span><span>{emp.date_of_joining || '--'}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-slate-500">Exit Reason</span><Badge variant="outline" className="text-xs">{emp.exit_reason || '--'}</Badge></div>
                  <div className="flex justify-between text-sm"><span className="text-slate-500">Last Working Date</span><span>{emp.last_working_date || '--'}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-slate-500">Archived On</span><span>{viewArchive.archived_at ? new Date(viewArchive.archived_at).toLocaleDateString('en-IN') : '--'}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-slate-500">Archived By</span><span>{viewArchive.archived_by || '--'}</span></div>
                </div>

                {exitF && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold text-[#0D2850]">Exit Interview</h4>
                    <div className="bg-blue-50 rounded-lg p-3 space-y-2 text-sm">
                      {exitF.reason_for_leaving && <div><span className="text-slate-500 text-xs block">Reason for leaving</span>{exitF.reason_for_leaving}</div>}
                      {exitF.experience_rating && <div><span className="text-slate-500 text-xs block">Experience rating</span>{exitF.experience_rating}/5</div>}
                      {exitF.what_liked_most && <div><span className="text-slate-500 text-xs block">What they liked most</span>{exitF.what_liked_most}</div>}
                      {exitF.what_could_improve && <div><span className="text-slate-500 text-xs block">What could improve</span>{exitF.what_could_improve}</div>}
                      {exitF.would_rejoin && <div><span className="text-slate-500 text-xs block">Would rejoin?</span>{exitF.would_rejoin}</div>}
                      {exitF.suggestions && <div><span className="text-slate-500 text-xs block">Suggestions</span>{exitF.suggestions}</div>}
                    </div>
                    <h4 className="text-sm font-semibold text-[#0D2850]">No-Dues Checklist</h4>
                    <div className="bg-slate-50 rounded-lg p-3 space-y-1.5 text-sm">
                      {[['id_card_returned', 'ID Card Returned'], ['laptop_returned', 'Laptop Returned'], ['assets_returned', 'Assets Returned'], ['pending_dues_cleared', 'Pending Dues Cleared'], ['knowledge_transfer_done', 'Knowledge Transfer Done']].map(([k, label]) => (
                        <div key={k} className="flex items-center justify-between">
                          <span className="text-slate-600">{label}</span>
                          <Badge className={exitF[k] ? 'bg-emerald-100 text-emerald-700 text-xs' : 'bg-red-100 text-red-700 text-xs'}>{exitF[k] ? 'Yes' : 'No'}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {!exitF && <p className="text-xs text-slate-400 text-center py-2">No exit form was submitted by this employee.</p>}

                {viewArchive.salary_structure && (
                  <details className="text-sm">
                    <summary className="text-xs font-semibold text-slate-500 cursor-pointer hover:text-[#0D2850]">Salary Structure</summary>
                    <div className="bg-slate-50 rounded-lg p-3 mt-1 space-y-1">
                      {Object.entries(viewArchive.salary_structure).filter(([k]) => !['id', 'employee_id', 'employee_name', 'employee_code', '_id'].includes(k)).map(([k, v]) => (
                        <div key={k} className="flex justify-between text-xs"><span className="text-slate-500">{k.replace(/_/g, ' ')}</span><span>{typeof v === 'boolean' ? (v ? 'Yes' : 'No') : v}</span></div>
                      ))}
                    </div>
                  </details>
                )}

                {viewArchive.leave_history && viewArchive.leave_history.length > 0 && (
                  <details className="text-sm">
                    <summary className="text-xs font-semibold text-slate-500 cursor-pointer hover:text-[#0D2850]">Leave History ({viewArchive.leave_history.length} records)</summary>
                    <div className="bg-slate-50 rounded-lg p-3 mt-1 max-h-40 overflow-y-auto space-y-1">
                      {viewArchive.leave_history.map((l, i) => (
                        <div key={i} className="flex justify-between text-xs border-b border-slate-100 pb-1"><span>{l.leave_type}</span><span>{l.start_date} - {l.end_date}</span><Badge className="text-[9px]" variant="outline">{l.status}</Badge></div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
